import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { writeAdProfessional } from '@archipro-website/tracker';
import { useProfessionalData } from '~/modules/professional/hook/use-professional-data';

/**
 * Records the professional whose ad brought the visitor to the site, when they
 * land on a library item detail page (professional, product, project, article).
 *
 * Every conversion is then compared against this professional, and reported as a
 * client-specific conversion when the two match. That distinction is what stops
 * a conversion with one professional being credited to another professional's
 * ad, which the advertising platform would otherwise do for any conversion by
 * that visitor inside the attribution window.
 *
 * Only stores when the landing page resolves a professional, so ads pointing at
 * non-professional pages produce no client-specific conversions.
 *
 * Call this once, from the root component.
 */
export function useAdProfessional() {
    const [searchParams] = useSearchParams();
    const professionalData = useProfessionalData();
    const utmData = useMemo(() => {
        const utmCampaign = searchParams.get('utm_campaign');
        const utmSource = searchParams.get('utm_source');
        const utmMedium = searchParams.get('utm_medium');
        return { utmCampaign, utmSource, utmMedium };
    }, [searchParams]);

    useEffect(() => {
        if (!professionalData) {
            return;
        }

        writeAdProfessional({
            professionalId: professionalData.professional.ID,
            utmCampaign: utmData.utmCampaign ?? '',
            utmSource: utmData.utmSource ?? '',
            utmMedium: utmData.utmMedium ?? '',
        });
    }, [utmData, professionalData]);
}
